import React from "react";
import { Ticket } from "@/lib/types/database";
import { useTimeEntries } from "@/lib/hooks/use-api";
import { Clock } from "lucide-react";
import Link from "next/link";
import { CollapsibleSection } from "@/components/shared/CollapsibleSection";

interface TicketTimeEntriesProps {
  ticket: Ticket;
}

export function TicketTimeEntries({ ticket }: TicketTimeEntriesProps) {
  // Fetch time entries for this ticket
  const {
    data: timeEntriesData,
    isLoading,
    error,
  } = useTimeEntries({
    ticketId: ticket.id,
    limit: 5, // Show only 5 entries in the summary view
  });

  const timeEntries = timeEntriesData || [];

  // Only render if there are time entries
  if (!isLoading && (!timeEntries || timeEntries.length === 0)) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-semibold text-card-foreground">Time Entries</h4>
        </div>
        <div className="bg-card-50 rounded-lg p-4">
          <div className="text-center py-4 text-gray-500">
            <Clock className="h-6 w-6 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Loading time entries...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-semibold text-card-foreground">Time Entries</h4>
        </div>
        <div className="bg-card-50 rounded-lg p-4">
          <div className="text-center py-4 text-red-600">
            <Clock className="h-6 w-6 mx-auto mb-2 text-red-300" />
            <p className="text-sm">Error loading time entries</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CollapsibleSection title="Time Entries" count={timeEntries.length} defaultCollapsed>
      <div className="flex items-center justify-end">
        <Link
          href={`/tickets/${ticket.id}?tab=time-entries`}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          View All ({timeEntries.length})
        </Link>
      </div>
      <div className="bg-card-50 rounded-lg p-4 mt-2">
        <div className="space-y-2">
          {timeEntries.slice(0, 5).map((entry: any, index: number) => (
            <div
              key={entry.id || index}
              className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
            >
              <div>
                <div className="text-sm font-medium text-card-foreground">{entry.description}</div>
                <div className="text-xs text-gray-500">
                  {new Date(entry.date_worked).toLocaleDateString()} • {entry.entry_type}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-card-foreground">{entry.hours}h</div>
                {entry.billable_rate && (
                  <div className="text-xs text-gray-500">${entry.billable_rate}/hr</div>
                )}
              </div>
            </div>
          ))}
          {timeEntries.length > 5 && (
            <div className="text-xs text-gray-500 text-center pt-2">
              And {timeEntries.length - 5} more entries...
            </div>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
}
